"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import { createClient } from "@/lib/supabase/client";

const MIN_YEAR = 1900;

/** Inline-editierbares, optionales Geburtsjahr (supabase/migrations/0027_birth_year.sql) -
 * gleiches Muster wie TaxAllowanceInput (speichert erst beim Verlassen des Felds, direkt
 * auf `profiles`). Freiwillig: leer lassen deaktiviert die Alters-Ansicht im Chart
 * darunter wieder, kein Pflichtfeld. */
export function BirthYearInput({ userId, value }: { userId: string; value: number | null }) {
  const router = useRouter();
  const [draft, setDraft] = useState(value !== null ? String(value) : "");
  const [isPending, setIsPending] = useState(false);

  async function handleBlur() {
    const trimmed = draft.trim();
    const currentYear = new Date().getUTCFullYear();

    if (trimmed === "") {
      if (value === null) return;
      setIsPending(true);
      const supabase = createClient();
      const { error } = await supabase.from("profiles").update({ birth_year: null }).eq("id", userId);
      setIsPending(false);
      if (!error) router.refresh();
      else setDraft(String(value));
      return;
    }

    const parsed = Number(trimmed);
    if (!Number.isInteger(parsed) || parsed < MIN_YEAR || parsed > currentYear) {
      setDraft(value !== null ? String(value) : "");
      return;
    }
    if (parsed === value) return;

    setIsPending(true);
    const supabase = createClient();
    const { error } = await supabase.from("profiles").update({ birth_year: parsed }).eq("id", userId);
    setIsPending(false);

    if (!error) {
      router.refresh();
    } else {
      setDraft(value !== null ? String(value) : "");
    }
  }

  return (
    <label className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
      <span className="inline-flex items-center gap-1">
        Geburtsjahr (optional)
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" aria-label="Erklärung: Geburtsjahr" className="text-primary hover:text-primary/80">
              <Info className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-72">
            Freiwillige Angabe, schaltet im Historie-Chart unten einen Umschalter frei, der die
            X-Achse statt nach Kalenderjahr nach deinem Alter beschriftet. Nur das Jahr wird
            gespeichert, kein volles Geburtsdatum.
          </TooltipContent>
        </Tooltip>
      </span>
      <Input
        type="number"
        min={MIN_YEAR}
        max={new Date().getUTCFullYear()}
        step="1"
        placeholder="z. B. 1990"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={handleBlur}
        disabled={isPending}
        aria-label="Geburtsjahr"
        className="w-24 bg-card text-right"
      />
    </label>
  );
}
